// =========================================================================
// Sincronización de la valoración inicial con el servidor.
//
// El defecto que esto cierra (ADR-020, 13-sep-2026): `PerfilClinicoInicial`
// sella la valoración EN LOCAL y manda `POST /valoracion` al servidor sin
// esperar respuesta — a propósito, para no atrapar a la persona en el
// formulario si la red falla. Pero antes de este fichero, un fallo de esa
// llamada solo se registraba con `console.warn`: la persona veía el flujo
// completarse con normalidad, y el dato de salud que creía haber guardado
// podía no haber llegado nunca al servidor, sin error, sin aviso.
//
// La línea roja AUTORITATIVA (bloqueo TCA, `exigirQuePuedaCalcular`) la
// impone el servidor sobre lo que de verdad recibe — así que un envío que
// nunca llega no es un detalle de UI, es el propio dato de salud perdido.
//
// Este módulo no cambia esa filosofía de no bloquear: sigue siendo
// fire-and-forget. Lo que añade es que un envío fallido queda marcado como
// pendiente (`RegistroInicial.sincronizadoEn` ausente) y se reintenta en
// cada inicio de sesión (`App.tsx`) hasta que el servidor lo confirme. Un
// 409 «ya sellado» cuenta como éxito — es la regla funcionando, no un fallo
// (ver `ApiClient.confirmarValoracionInicial`).
// =========================================================================
import { ApiClient, ApiError } from '@/services/api-client';
import { getDataMode } from '@/config/data-mode';
import { leerRegistroInicial, marcarRegistroSincronizado } from './almacen';
import { estaSellado, estaSincronizado } from './registro-inicial';

/**
 * Intenta sellar en el servidor la valoración inicial ya sellada en local, si
 * todavía no hay confirmación. No hace nada si no hay nada que sincronizar,
 * si no se está en modo API, o si ya está sincronizada.
 *
 * Fire-and-forget a propósito: quien llame no debe esperar a esta función
 * para continuar (login, navegación) — un reintento fallido simplemente
 * queda pendiente para el próximo.
 */
export async function reintentarSincronizacionValoracion(): Promise<void> {
  if (getDataMode() !== 'api' || !ApiClient.isConfigured()) return;

  const registro = leerRegistroInicial();
  if (!estaSellado(registro) || estaSincronizado(registro)) return;

  try {
    await ApiClient.confirmarValoracionInicial(registro.datos);
    marcarRegistroSincronizado(new Date().toISOString());
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      // Ya estaba sellada en el servidor desde un envío anterior: es el
      // resultado correcto, no un fallo — se marca sincronizada igual.
      marcarRegistroSincronizado(new Date().toISOString());
      return;
    }
    console.warn(
      '[valoracion] no se pudo confirmar en el servidor; se reintentará en el próximo inicio de sesión:',
      err,
    );
  }
}
